import { type ReactNode, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Combobox } from '@/components/core/Combobox';
import { DateInput } from '@/components/core/DateInput';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { HTMLInput } from '@/components/core/HTMLInput';
import { Input } from '@/components/core/Input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { Uploader, type UploadItem } from '@/components/core/Uploader';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { useDataModel } from '@/hooks/useDataModel';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';

import {
    createTask,
    type NewTask,
    type SavedTask,
    type TaskAttachment,
    TaskAttachmentCommentError,
    uploadTaskAttachment,
    useTaskLists,
    useTaskUsers,
} from './createTask.queries';

/** `assets` accepts single uploads up to 32 MB. */
const MAX_ATTACHMENT_SIZE = 32 * 1024 * 1024;

type CreateTaskTarget = {
    /** Up to 25 checked primary keys. */
    items?: string[];
    /** Saved selection for global select-all or more than 25 keys. */
    selectionId?: string;
    taskListId?: number;
};

interface CreateTaskDialogProps {
    open: boolean;
    target: CreateTaskTarget;
    onClose: (task?: SavedTask) => void;
    /** The dialog has hidden itself while the save is still running. */
    onHidden?: () => void;
    /** A failed save showed the dialog again. */
    onShown?: () => void;
}

type Option = { value: string; label: string };

type DataModelWithTaskTags = { getTaskTags?: () => string[] | undefined };

/** DateInput portals into `#root-portal`. The tasks page does not have that node, and the picker throws without it. */
const ensureDatePortal = () => {
    if (typeof document === 'undefined') {
        return;
    }
    let portal = document.getElementById('root-portal');
    if (!portal) {
        portal = document.createElement('div');
        portal.id = 'root-portal';
        document.body.appendChild(portal);
    }
    // A modal sets `pointer-events: none` on the body. The calendar is portaled
    // here, so it has to opt back in or a mouse click never lands.
    portal.style.pointerEvents = 'auto';
};

type NoticeLevel = 'success' | 'warning' | 'error';

/**
 * The Angular page notification outlives this dialog. The React toast is
 * removed with the dialog when the task list, item editor, or grouping mounts it.
 */
const notifyShell = (
    level: NoticeLevel,
    key: string,
    fallback: (message: string) => void,
    translate: (messageKey: string) => string,
) => {
    try {
        const growl = getInjector()?.get?.('growl') as Partial<Record<NoticeLevel, (message: string) => void>> | undefined;
        const show = growl?.[level];
        if (typeof show === 'function') {
            show(key);
            return;
        }
    } catch {
        // Storybook and unit tests have no Angular shell.
    }
    fallback(translate(key));
};

const notifySelfAssigned = (task: SavedTask) => {
    try {
        const rootScope = getRootScope(getInjector());
        if (rootScope.userId != null && String(rootScope.userId) === task.assignee) {
            rootScope.$broadcast?.('selfTaskCreated', task);
        }
    } catch {
        // Storybook and unit tests have no Angular shell.
    }
};

/** Assignee, tags, and the due-date calendar are portaled outside the dialog. */
const isPortaledFieldEvent = (event: { target: EventTarget | null }) => (
    event.target instanceof Element
    && !!event.target.closest('.select-content, .react-datepicker, .react-datepicker-popper, #root-portal')
);

/** Matches the basic CKEditor toolbar: bold, italic, lists, and links. */
const NOTES_TOOLBAR = {
    options: ['inline', 'list', 'link'],
    inline: { options: ['bold', 'italic'] },
    list: { options: ['unordered', 'ordered'] },
    link: { options: ['link', 'unlink'] },
};

const FormRow = ({ htmlFor, label, children }: { htmlFor: string; label: string; children: ReactNode }) => (
    <div>
        <label htmlFor={htmlFor} className="tw-mb-1 tw-block tw-text-t-sm tw-font-medium tw-text-t-primary">
            {label}
        </label>
        {children}
    </div>
);

/**
 * React port of `tpl/tasks-create-task.tpl.html` and
 * `CreateTaskController`. Saving posts the task with either `items` or
 * `selectionId`. Attachments upload as soon as they are added and are
 * stored as the first comment of the new task.
 */
const CreateTaskDialog = ({ open, target, onClose, onHidden, onShown }: CreateTaskDialogProps) => {
    ensureDatePortal();
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const { data: taskLists = [] } = useTaskLists({ enabled: open });
    const { data: users = [] } = useTaskUsers({ enabled: open });

    const [title, setTitle] = useState('');
    const [taskListId, setTaskListId] = useState<number | undefined>(target.taskListId);
    const [dueDate, setDueDate] = useState<Date | undefined>();
    const [assignee, setAssignee] = useState<Option | null>(null);
    const [notes, setNotes] = useState('');
    const [tags, setTags] = useState<Option[]>([]);
    const [uploads, setUploads] = useState<UploadItem[]>([]);
    const [attachments, setAttachments] = useState<Record<string, TaskAttachment[]>>({});
    const [submitted, setSubmitted] = useState(false);
    const submittedRef = useRef(false);

    const userOptions = useMemo<Option[]>(
        () => users.map((user) => ({ value: user.userId, label: user.displayName ?? user.userId })),
        [users],
    );
    const tagOptions = useMemo<Option[]>(() => {
        const taskTags = (dataModel as DataModelWithTaskTags | undefined)?.getTaskTags?.() ?? [];
        return taskTags.map((tag) => ({ value: tag, label: tag }));
    }, [dataModel]);

    const setUpload = (id: string, patch: Partial<UploadItem>) => {
        setUploads((current) => current.map((upload) => (upload.id === id ? { ...upload, ...patch } : upload)));
    };

    const startUpload: (upload: UploadItem) => void = (upload) => {
        uploadTaskAttachment(upload.file).then((assets) => {
            setAttachments((current) => ({ ...current, [upload.id]: assets }));
            setUpload(upload.id, { status: 'success', progress: 100 });
        }).catch((error) => {
            logger.error('Task attachment upload failed', error);
            setUpload(upload.id, { status: 'error' });
        });
    };

    const onFilesAdded = (files: File[]) => {
        const added = files.map((file): UploadItem => ({
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
            file,
            status: 'uploading',
            startedAt: Date.now(),
        }));
        setUploads((current) => [...current, ...added]);
        added.forEach(startUpload);
    };

    const onFileRemove = (item: UploadItem) => {
        setUploads((current) => current.filter((upload) => upload.id !== item.id));
        setAttachments((current) => Object.fromEntries(
            Object.entries(current).filter(([id]) => id !== item.id),
        ));
    };

    const onFileRetry = (item: UploadItem) => {
        setUpload(item.id, { status: 'uploading', startedAt: Date.now() });
        startUpload(item);
    };

    const save = async () => {
        if (submittedRef.current) {
            return;
        }
        if (!title.trim()) {
            notifyShell('warning', 'TASK.MISSING_TASK_TITLE', toast.warning, t);
            return;
        }
        if (uploads.some((upload) => upload.status !== 'success')) {
            notifyShell('warning', 'NOTIFY.FILES_STILL_NOT_UPLOADED', toast.warning, t);
            return;
        }

        const task: NewTask = {
            title,
            taskListId,
            dueDate: dueDate?.toISOString(),
            assignee: assignee?.value,
            notes: notes || undefined,
            tags: tags.length > 0 ? tags.map((tag) => tag.value) : undefined,
            ...(target.selectionId ? { selectionId: target.selectionId } : { items: target.items }),
        };
        const files = Object.values(attachments).flat();

        submittedRef.current = true;
        setSubmitted(true);
        onHidden?.();
        try {
            const saved = await createTask(task, files);
            notifySelfAssigned(saved);
            notifyShell('success', 'TASK.ADD_TASK_SUCCESS_MESSAGE', toast.success, t);
            onClose(saved);
        } catch (error) {
            if (error instanceof TaskAttachmentCommentError) {
                notifySelfAssigned(error.saved);
                notifyShell('success', 'TASK.ADD_TASK_SUCCESS_MESSAGE', toast.success, t);
                notifyShell('error', 'ERROR_OCCURRED', toast.error, t);
                onClose(error.saved);
                return;
            }
            logger.error('Creating task failed', error);
            submittedRef.current = false;
            setSubmitted(false);
            onShown?.();
            notifyShell('error', 'ERROR_OCCURRED', toast.error, t);
        }
    };

    return (
        <Dialog open={open && !submitted} onOpenChange={(next) => { if (!next && !submittedRef.current) onClose(); }}>
            <DialogContent
                variant="large"
                className="tw-max-h-[calc(100dvh-40px)] tw-overflow-hidden"
                onPointerDownOutside={(event) => {
                    if (isPortaledFieldEvent(event)) {
                        event.preventDefault();
                    }
                }}
                onInteractOutside={(event) => {
                    if (isPortaledFieldEvent(event)) {
                        event.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogHeaderDefault>{t('TASK.ADDING_TASK')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody scrollable>
                    <FormResetProvider>
                        <div className="tw-flex tw-flex-col tw-gap-4">
                            <FormRow htmlFor="taskTitle" label={t('TASK.TITLE')}>
                                <Input
                                    id="taskTitle"
                                    autoFocus
                                    value={title}
                                    onChange={(event) => setTitle(event.target.value)}
                                />
                            </FormRow>
                            <FormRow htmlFor="taskList" label={t('TASK.TASK_LIST')}>
                                <Select
                                    value={taskListId == null ? undefined : String(taskListId)}
                                    onValueChange={(value) => setTaskListId(Number(value))}
                                >
                                    <SelectTrigger id="taskList">
                                        <SelectValue placeholder={t('TASK.DEFAULT_TASK_LIST')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {taskLists.map((taskList) => (
                                            <SelectItem key={taskList.id} value={String(taskList.id)}>
                                                {taskList.title || t('TASK.DEFAULT_TASK_LIST')}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormRow>
                            <FormRow htmlFor="taskDueDate" label={t('TASK.DUE_DATE')}>
                                <DateInput
                                    id="taskDueDate"
                                    selectedDate={dueDate}
                                    onChange={(value) => setDueDate(value ?? undefined)}
                                />
                            </FormRow>
                            <FormRow htmlFor="taskAssignee" label={t('TASK.ASSIGNEE')}>
                                <Combobox
                                    inputId="taskAssignee"
                                    isClearable
                                    options={userOptions}
                                    value={assignee}
                                    placeholder=""
                                    onChange={(option: Option | null) => setAssignee(option)}
                                />
                            </FormRow>
                            <FormRow htmlFor="taskNotes" label={t('TASK.NOTES')}>
                                <HTMLInput
                                    id="taskNotes"
                                    rows={4}
                                    value={notes}
                                    onChange={setNotes}
                                    toolbar={NOTES_TOOLBAR}
                                    toolbarCustomButtons={[]}
                                />
                            </FormRow>
                            <FormRow htmlFor="taskTags" label={t('TASK.TAGS')}>
                                <Combobox
                                    inputId="taskTags"
                                    isMulti
                                    options={tagOptions}
                                    value={tags}
                                    placeholder=""
                                    onChange={(options: readonly Option[] | null) => setTags([...(options ?? [])])}
                                />
                            </FormRow>
                            <div>
                                <span className="tw-mb-1 tw-block tw-text-t-sm tw-font-medium tw-text-t-primary">
                                    {t('TASK.ATTACHMENT')}
                                </span>
                                <Uploader
                                    isMultiple
                                    maxFileSize={MAX_ATTACHMENT_SIZE}
                                    uploads={uploads}
                                    isDisabled={submitted}
                                    onFilesAdded={onFilesAdded}
                                    onFileRemove={onFileRemove}
                                    onFileRetry={onFileRetry}
                                />
                            </div>
                        </div>
                    </FormResetProvider>
                </DialogBody>
                <DialogFooter>
                    <Button
                        id="create-task-add"
                        type="button"
                        variant="default"
                        disabled={submitted}
                        onClick={() => { void save(); }}
                    >
                        {t('TASK.ADD')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => onClose()}>
                        {t('CANCEL')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

CreateTaskDialog.displayName = 'CreateTaskDialog';

export type { CreateTaskDialogProps, CreateTaskTarget };
export { CreateTaskDialog };
